import type * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CaretDown as CaretDownIcon } from "@phosphor-icons/react/CaretDown";

import { Option } from "../../../../option";
import type { BaseComponentProps, QueryConfig } from "../types";

export interface QuerySelectorProps extends BaseComponentProps {
	/** Available query configurations */
	queries: QueryConfig[];
	/** Currently selected query */
	value: QueryConfig | null;
	/** Callback when query selection changes */
	onChange: (query: QueryConfig | null) => void;
	/** Loading state */
	loading?: boolean;
	/** Placeholder text */
	placeholder?: string;
}

export function QuerySelector({
	queries,
	value,
	onChange,
	loading = false,
	error,
	placeholder = "Select query",
}: QuerySelectorProps): React.JSX.Element {
	const handleChange = (
		_: React.SyntheticEvent,
		newValue: QueryConfig | null,
	) => {
		onChange(newValue);
	};

	return (
		<Stack spacing={1}>
			<Typography
				variant="overline"
				color="text.secondary">
				Select Query
			</Typography>
			<Autocomplete
				value={value}
				onChange={handleChange}
				options={queries}
				getOptionLabel={(option) => option.displayName}
				isOptionEqualToValue={(option, val) => option.id === val.id}
				disabled={loading}
				popupIcon={<CaretDownIcon size={16} />}
				renderInput={(params) => (
					<FormControl
						fullWidth
						error={!!error}>
						<OutlinedInput
							ref={params.InputProps.ref}
							placeholder={placeholder}
							endAdornment={params.InputProps.endAdornment}
						/>
					</FormControl>
				)}
				renderOption={(props, option) => (
					<Option
						{...props}
						key={option.id}
						value={option.id}>
						<Stack>
							<Typography variant="body2">{option.displayName}</Typography>
							{option.description && (
								<Typography
									variant="caption"
									color="text.secondary">
									{option.description}
								</Typography>
							)}
						</Stack>
					</Option>
				)}
			/>
			{error && (
				<Typography
					variant="caption"
					color="error">
					{error}
				</Typography>
			)}
		</Stack>
	);
}
